import { Head } from 'components/Head';
import { useEffect, useMemo, useState } from 'react';

const TERMS_KEY = 'ideanest-accepted-terms';

const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

const loadImages = () => {
  const context = require.context('../../../assets/inspiration', true, /\.(png|jpe?g|webp)$/i);

  const seen = new Set();

  return shuffle(
    context
      .keys()
      .map((key) => {
        const mod = context(key);
        const src = mod.default || mod;

        const parts = key.replace('./', '').split('/');
        const filename = parts.pop();
        const category = parts.join(' / ');

        const caption = filename.replace(/\.(png|jpe?g|webp)$/i, '').replace(/[-_]/g, ' ');

        return { src, category, caption };
      })
      .filter((img) => {
        if (seen.has(img.src)) return false;
        seen.add(img.src);
        return true;
      })
  );
};

export const Landing = () => {
  const [images] = useState(loadImages);
  const [activeIndex, setActiveIndex] = useState(null);
  const [search, setSearch] = useState('');
  const [slideshow, setSlideshow] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(localStorage.getItem(TERMS_KEY) === 'yes');

  const filtered = useMemo(() => {
    return images.filter(
      (img) =>
        img.category.toLowerCase().includes(search.toLowerCase()) ||
        img.caption.toLowerCase().includes(search.toLowerCase())
    );
  }, [images, search]);

  /* Keyboard navigation */
  useEffect(() => {
    if (activeIndex === null) return;

    const handler = (e) => {
      if (e.key === 'ArrowRight') setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
      if (e.key === 'ArrowLeft') setActiveIndex((i) => Math.max(i - 1, 0));
      if (e.key === 'Escape') setActiveIndex(null);
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeIndex, filtered.length]);

  /* Slideshow mode */
  useEffect(() => {
    if (!slideshow || activeIndex === null) return;

    const id = setInterval(() => {
      setActiveIndex((i) => (i === filtered.length - 1 ? 0 : i + 1));
    }, 3000);

    return () => clearInterval(id);
  }, [slideshow, activeIndex, filtered.length]);

  /* Terms Gate */
  if (!acceptedTerms) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-100">
        <div className="max-w-lg bg-white p-8 rounded-2xl shadow">
          <h1 className="text-2xl font-bold">Welcome to Inpiro</h1>
          <p className="mt-4 text-gray-600 text-sm">
            Inspiro is a public inspiration space for learning and creativity.
          </p>

          <ul className="list-disc ml-5 mt-4 text-sm text-gray-600">
            <li>
              You must confirm you are at least <strong>7 years old</strong>
            </li>
            <li>Content is for educational inspiration only</li>
            <li>No redistribution without permission</li>
          </ul>

          <button
            onClick={() => {
              localStorage.setItem(TERMS_KEY, 'yes');
              setAcceptedTerms(true);
            }}
            className="mt-6 w-full bg-black text-white py-3 rounded-xl"
          >
            I Understand & Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Head title="Inspiro — Inspiration Wall" />

      <header className="sticky top-0 z-40 bg-white border-b px-4 py-3 flex gap-4">
        <strong>Inspiro</strong>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search ideas…"
          className="ml-auto px-4 py-2 rounded-full border bg-gray-50"
        />

        {activeIndex !== null && (
          <button onClick={() => setSlideshow(!slideshow)}>
            {slideshow ? 'Stop' : 'Slideshow'}
          </button>
        )}
      </header>

      <main className="columns-2 sm:columns-3 md:columns-4 lg:columns-5 gap-4 p-4">
        {filtered.map((img, i) => (
          <div
            key={i}
            className="mb-4 cursor-pointer break-inside-avoid"
            onClick={() => setActiveIndex(i)}
          >
            <img src={img.src} loading="lazy" className="rounded-2xl w-full" alt={img.caption} />
            <p className="text-xs text-gray-600 mt-1">{img.caption}</p>
          </div>
        ))}
      </main>

      {activeIndex !== null && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"
          onClick={() => setActiveIndex(null)}
        >
          <img
            src={filtered[activeIndex].src}
            className="max-h-[90vh] rounded-2xl bg-white"
            onClick={(e) => e.stopPropagation()}
            alt=""
          />
        </div>
      )}
    </>
  );
};
